import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { trackShare, type ShareContentType } from '../analytics';
import { canShareNatively, shareLink } from '../utils/share';

import { ShareIcon } from './icons';

export interface ShareButtonProps {
  title: string;
  /** An app path, e.g. from `paths`; made absolute here. */
  path: string;
  contentType: ShareContentType;
  itemId: string | number;
  className?: string;
}

/** How long the "link copied" note stays up where the link was copied. */
const COPIED_NOTE_MS = 2000;

export function ShareButton({
  title,
  path,
  contentType,
  itemId,
  className = '',
}: ShareButtonProps): ReactElement {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_NOTE_MS);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const share = (): void => {
    // Pushed as the sheet opens; a dismissed sheet still counts, since the
    // browser never says what the visitor did in it.
    trackShare({
      contentType,
      itemId: String(itemId),
      method: canShareNatively() ? 'native' : 'copy_link',
    });

    void shareLink({ title, url: new URL(path, window.location.origin).toString() }).then(
      (outcome) => {
        if (outcome.method === 'copy_link' && outcome.status === 'copied') {
          setCopied(true);
        }
      },
    );
  };

  return (
    <span className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        onClick={share}
        aria-label={t('common.share')}
        className="text-fog-100 transition-opacity hover:opacity-70"
      >
        <ShareIcon className="size-6" />
      </button>
      {copied && (
        <span
          role="status"
          className="absolute top-full right-0 z-10 mt-2 rounded-lg bg-ink-700 px-3 py-1.5 text-xs whitespace-nowrap text-fog-100"
        >
          {t('common.linkCopied')}
        </span>
      )}
    </span>
  );
}
